import { formatNumber } from '@angular/common';
import { Pipe, PipeTransform } from '@angular/core';

import { Locale } from '@optical/types';
import { Unit, UnitRegistry } from '@optical/utils';

/** Conversion options passed to the `convert` function. */
export interface ConversionOptions {
  unit: Unit;
  locale?: Locale;
  digitsInfo?: string;
}

/**
 * Converts a value to the given unit.
 * If a `from` unit is provided, converts the value from the `from` unit to the base unit first.
 *
 * @param value - The value to convert.
 * @param to - The unit to convert to.
 * @param from - The unit to convert from.
 * @returns The converted value.
 */
export function convertV2(value: number, to: Unit, from?: Unit): number {
  if (from) {
    value = value / UnitRegistry[from].baseFactor;
  }

  const converted = value * UnitRegistry[to].baseFactor;

  return converted;
}

@Pipe({
  name: 'convert',
})
export class ConvertPipe implements PipeTransform {
  transform(value: number, to: Unit, locale = Locale.EN_US, digitsInfo = '1.0-1'): string {
    return convert(value, to, locale, digitsInfo);
  }
}

/** Mapping of `Locale` to the locale string used by Angular's `formatNumber`. */
export const ANGULAR_LOCALE_MAP: Record<Locale, string> = {
  [Locale.DE_DE]: 'de-DE',
  [Locale.EN_GB]: 'en-GB',
  [Locale.EN_US]: 'en-US',
};

/**
 * Converts a value to a string representation of the given unit.
 *
 * @param value - The value to convert.
 * @param to - The unit to convert to.
 * @param locale - The locale to use for formatting.
 * @param digitsInfo - The digits info to use for formatting.
 * @returns The converted value as a string.
 */
export function convert(
  value: number,
  to: Unit,
  locale = Locale.EN_US,
  digitsInfo = '1.0-1',
): string {
  if (to === 'HH:mm:ss') {
    return formatDuration(value, 'HH:mm:ss');
  }

  if (to === 'HH:mm') {
    return formatDuration(value, 'HH:mm');
  }

  const converted = value * UnitRegistry[to].baseFactor;
  const symbol = UnitRegistry[to].symbol;

  const formatted = formatNumber(converted, ANGULAR_LOCALE_MAP[locale], digitsInfo);

  if (symbol) {
    return `${formatted} ${symbol}`;
  }

  return formatted;
}

/**
 * Formats a duration value in seconds to a string representation.
 *
 * @param value - The duration value in seconds.
 * @param format - The format to use for formatting the duration.
 * @returns The formatted duration string.
 */
function formatDuration(totalSeconds: number, format: 'HH:mm:ss' | 'HH:mm' = 'HH:mm:ss'): string {
  const hours = Math.floor(totalSeconds / 3600);
  const minutes = Math.floor((totalSeconds % 3600) / 60);
  const seconds = Math.floor(totalSeconds % 60);

  const h = String(hours).padStart(2, '0');
  const m = String(minutes).padStart(2, '0');

  if (format === 'HH:mm:ss') {
    const s = String(seconds).padStart(2, '0');

    return `${h}:${m}:${s}`;
  }

  return `${h}:${m}`;
}
